import { defineStore } from 'pinia'
import { getDisasterVisualization } from '@/api/disasterVisualization/getDisasterVisualizationApiGwFuncs'
import { ref } from 'vue'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

interface InvInspectionResultState {
  allCount: number
  uninvestigated: number
  noDamage: number
  partiallyDamaged: number
  semiPartiallyDestroyed: number
  partiallyDestroyed: number
  halfTotallyDestroyed: number
  almostTotallyDestroyed: number
  totallyDestroyed: number
  undecidable: number
  floodingUnderFloor: number
  floodingAboveFloor: number
}

interface EmergencyResultState {
  allCount: number
  uninvestigated: number
  inspected: number
  warning: number
  danger: number
}

interface AiCollapseResultState {
  allCount: number
  uninvestigated: number
  notTotallyDestroyed: number
  totalCollapse: number
}

interface InundationDamageState {
  allCount: number
  uninvestigated: number
  nothingExternalDamageUnderfloor: number
  nothingExternalDamageOnFloor: number
  withExternalDamageUnderfloor: number
  withExternalDamageLessThan_0_5mFloor: number
  withExternalDamageLessThan_1_0mFloor: number
  withExternalDamageLessThan_1_8mFloor: number
  withExternalDamageMoreThan_1_8mFloor: number
}

export const useMapPointCloudStore = defineStore('mapPointCloudStore', () => {
  /** 住家被害認定調査の結果データ */
  const invInspectionResult = ref<InvInspectionResultState | null>(null)

  /** 応急危険度判定の結果データ */
  const emergencyResult = ref<EmergencyResultState | null>(null)

  /** AI倒壊判定の結果データ */
  const aiCollapseResult = ref<AiCollapseResultState | null>(null)

  /** 浸水深の結果データ */
  const inundationDamage = ref<InundationDamageState | null>(null)

  /**
   * 円グラフデータ取得APIを実行し、取得結果をstateに格納する
   * @param govCode 自治体コード
   * @param disasterId 災害ID
   * @returns なし
   */
  const fetchData = async (govCode: string, disasterId: string): Promise<void> => {
    try {
      const response = await getDisasterVisualization({
        government_code: govCode,
        disaster_id: disasterId
      })

      invInspectionResult.value = {
        allCount: response.result.inv_inspection_result.all_count,
        uninvestigated: response.result.inv_inspection_result.uninvestigated,
        noDamage: response.result.inv_inspection_result.no_damage,
        partiallyDamaged: response.result.inv_inspection_result.partially_damaged,
        semiPartiallyDestroyed: response.result.inv_inspection_result.semi_partially_destroyed,
        partiallyDestroyed: response.result.inv_inspection_result.partially_destroyed,
        halfTotallyDestroyed: response.result.inv_inspection_result.half_totally_destroyed,
        almostTotallyDestroyed: response.result.inv_inspection_result.almost_totally_destroyed,
        totallyDestroyed: response.result.inv_inspection_result.totally_destroyed,
        undecidable: response.result.inv_inspection_result.undecidable,
        floodingUnderFloor:
          response.result.inundation_damage.nothing_external_damage_underfloor +
          response.result.inundation_damage.with_external_damage_underfloor,
        floodingAboveFloor:
          response.result.inundation_damage.nothing_external_damage_on_floor +
          response.result.inundation_damage.with_external_damage_less_than_0_5m_floor +
          response.result.inundation_damage.with_external_damage_less_than_1_0m_floor +
          response.result.inundation_damage.with_external_damage_less_than_1_8m_floor +
          response.result.inundation_damage.with_external_damage_more_than_1_8m_floor
      }
      emergencyResult.value = {
        allCount: response.result.emergency_result.all_count,
        uninvestigated: response.result.emergency_result.uninvestigated,
        inspected: response.result.emergency_result.inspected,
        warning: response.result.emergency_result.warning,
        danger: response.result.emergency_result.danger
      }
      aiCollapseResult.value = {
        allCount: response.result.ai_collapse_result.all_count,
        uninvestigated: response.result.ai_collapse_result.uninvestigated,
        notTotallyDestroyed: response.result.ai_collapse_result.not_totally_destroyed,
        totalCollapse: response.result.ai_collapse_result.total_collapse
      }
      inundationDamage.value = {
        allCount: response.result.inundation_damage.all_count,
        uninvestigated: response.result.inundation_damage.uninvestigated,
        nothingExternalDamageUnderfloor:
          response.result.inundation_damage.nothing_external_damage_underfloor,
        nothingExternalDamageOnFloor:
          response.result.inundation_damage.nothing_external_damage_on_floor,
        withExternalDamageUnderfloor:
          response.result.inundation_damage.with_external_damage_underfloor,
        withExternalDamageLessThan_0_5mFloor:
          response.result.inundation_damage.with_external_damage_less_than_0_5m_floor,
        withExternalDamageLessThan_1_0mFloor:
          response.result.inundation_damage.with_external_damage_less_than_1_0m_floor,
        withExternalDamageLessThan_1_8mFloor:
          response.result.inundation_damage.with_external_damage_less_than_1_8m_floor,
        withExternalDamageMoreThan_1_8mFloor:
          response.result.inundation_damage.with_external_damage_more_than_1_8m_floor
      }
    } catch (err) {
      console.error(err)
      throw new GlobalError([ERROR_CODES.FETCH_DONUT_DATA_FAILED])
    }
  }

  /**
   * stateをリセットする
   * @param なし
   * @returns なし
   */
  const reset = (): void => {
    invInspectionResult.value = null
    emergencyResult.value = null
    aiCollapseResult.value = null
  }

  return {
    invInspectionResult,
    emergencyResult,
    aiCollapseResult,
    fetchData,
    reset
  }
})
